import { difference, flatten, map, uniq } from "lodash/fp";

import { hashFunc } from "@app/analytics/utils";
import type { Tag } from "@app/common/types";

const DARK_TAG_FONT_COLOR = "#FFF";

export interface TagColor {
  backgroundColor: string;
  color: string;
}

const zipColors = (lightColors: string[], darkColors: string[]): TagColor[] => {
  const zippedColors = lightColors.map((lightColor, i) => {
    const darkColor = darkColors[i];
    return [
      { backgroundColor: darkColor, color: DARK_TAG_FONT_COLOR },
      { backgroundColor: lightColor, color: darkColor },
    ];
  });

  return flatten(zippedColors);
};

const LIGHT_TAG_COLORS = [
  "#E5BEEB", // avatar - magenta
  "#F5C6CC", // avatar - red
  "#F5D4C6", // avatar - red-orange
  "#F2EBC4", // avatar - yellow
  "#E1F2C4", // avatar - yellow-green
  "#C6F5E1", // avatar - green
  "#C6D9F5", // avatar - blue
  "#D5C6F5", // avatar - purple
];

const DARK_TAG_COLORS = [
  "#B734CC", // avatar - magenta
  "#CC3446", // avatar - red
  "#CC6134", // avatar - red-orange
  "#CCB434", // avatar - yellow
  "#80B22D", // avatar - yellow-green
  "#2DB27B", // avatar - green
  "#3471CC", // avatar - blue
  "#6534CC", // avatar - purple
];

const ALL_COLORS = [...LIGHT_TAG_COLORS, ...DARK_TAG_COLORS];

export const TAG_COLORS = zipColors(LIGHT_TAG_COLORS, DARK_TAG_COLORS);

export const isDarkTagColor = (color: string) =>
  DARK_TAG_COLORS.includes(color);

export const getTagFontColorFromColor = (color: string) => {
  if (isDarkTagColor(color)) return DARK_TAG_FONT_COLOR;

  return (
    DARK_TAG_COLORS[LIGHT_TAG_COLORS.indexOf(color)] ?? DARK_TAG_FONT_COLOR
  );
};

export const getNewTagColor = (name: string, existingTags: Tag[]) => {
  const existingTagColors = uniq(map("color", existingTags));

  // prioritize colors that aren't being used
  const remainingColors =
    existingTagColors.length === ALL_COLORS.length
      ? ALL_COLORS
      : difference(ALL_COLORS, existingTagColors);

  return hashFunc(name, remainingColors);
};
